import { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { api, session } from '../lib/api';
import { ensureGuestSession, isGuestUser } from '../lib/session';
import { exchangeOptionalAccount } from '../lib/accountSync';
import { disablePush } from '../lib/push';
import { usePwaInstall } from '../hooks/usePwaInstall';
import { beginVoiceCapture, undoVoiceBatch } from '../lib/voiceCaptureService';
import { markNoteReviewed } from '../lib/noteReview';
import { showSavedCapture } from '../components/SavedCaptureFeedback';
import { createTypedSave } from '../lib/noteScheduling';

const Context = createContext(null);
export const useApp = () => useContext(Context);
export function AppProvider({ children }) {
  const [user, setUser] = useState(null), [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(true), [error, setError] = useState('');
  const [authOpen, setAuthOpen] = useState(false), [editing, setEditing] = useState(null);
  const [editingBatch, setEditingBatch] = useState(null);
  const [online, setOnline] = useState(navigator.onLine);
  const pwa = usePwaInstall();
  const navigate = useNavigate(); const booted = useRef(false), connecting = useRef(null), userRef = useRef(null);
  const guest = user ? isGuestUser(user) : true;
  const refresh = useCallback(async () => {
    const token = session.get(); if (!token) return;
    try {
      const records = await api.notes();
      if (session.get() === token) { setNotes(records.filter(n => !n.deleted_at)); setError(''); }
    } catch (e) { setError(e.message); }
  }, []);
  const connect = useCallback(() => {
    if (connecting.current) return connecting.current;
    setLoading(true); setError('');
    connecting.current = (async () => {
      try {
        await ensureGuestSession(api.guest);
        const token = session.get();
        const data = await api.me();
        if (!data?.user) throw new Error('Could not restore your saves. Please retry.');
        if (session.get() !== token) return null;
        if (typeof data.user.is_guest === 'boolean') session.setKind(data.user.is_guest ? 'guest' : 'account');
        userRef.current = data.user; setUser(data.user);
        await refresh();
        if (!isGuestUser(data.user)) session.finishMigration();
        return data.user;
      } catch (e) {
        const message = e.name === 'SecurityError' || e.name === 'QuotaExceededError'
          ? 'Device storage is unavailable. Allow site storage so your guest saves can stay connected.' : e.message;
        setError(message); throw new Error(message);
      } finally { connecting.current = null; setLoading(false); }
    })();
    return connecting.current;
  }, [refresh]);
  useEffect(() => {
    if (booted.current) return; booted.current = true;
    (async () => {
      const query = new URLSearchParams(window.location.search), hash = new URLSearchParams(window.location.hash.slice(1));
      const id = query.get('session_id') || hash.get('session_id');
      let syncError = '';
      if (id) {
        window.history.replaceState(null, '', '/auth/callback');
        try { await exchangeOptionalAccount(id); }
        catch (e) { syncError = e.message; }
        navigate('/', { replace: true });
      } else if (window.location.pathname === '/auth/callback') {
        navigate('/', { replace: true });
        syncError = 'Account sync was not completed. You can continue as a guest.';
      }
      try { await connect(); } catch { /* Inline retry, never a sign-in wall. */ }
      if (syncError) toast.error(syncError);
    })();
  }, [connect, navigate]);
  useEffect(() => {
    const unavailable = () => {
      userRef.current = null; setUser(null); setNotes([]);
      setError('Could not restore your saved session. Your device identifier has been kept. Please retry.');
    };
    const connectivity = () => { setOnline(navigator.onLine); if (navigator.onLine) connect().catch(() => {}); };
    const focus = () => { if (document.visibilityState === 'visible') connect().catch(() => {}); };
    const storage = e => {
      if (e.key === 'tts_web_session') {
        userRef.current = null; setUser(null); setNotes([]); setEditing(null);
        connect().catch(() => {});
      }
    };
    window.addEventListener('tts-session-unavailable', unavailable);
    window.addEventListener('online', connectivity); window.addEventListener('offline', connectivity);
    window.addEventListener('storage', storage); document.addEventListener('visibilitychange', focus);
    return () => {
      window.removeEventListener('tts-session-unavailable', unavailable);
      window.removeEventListener('online', connectivity); window.removeEventListener('offline', connectivity);
      window.removeEventListener('storage', storage); document.removeEventListener('visibilitychange', focus);
    };
  }, [connect]);
  const ensureAccess = async () => {
    if (userRef.current && session.get()) return true;
    return Boolean(await connect());
  };
  const create = async (text, method = 'typed') => {
    if (!await ensureAccess()) throw new Error('Your device session is still connecting. Please retry.');
    const token = session.get();
    const onChange = ({ upsert, remove }) => {
      if (session.get() !== token) return;
      const replaced = new Set([...upsert.map(n => n.id), ...remove]);
      setNotes(prev => [...upsert, ...prev.filter(n => !replaced.has(n.id))]);
    };
    let batch;
    if (method === 'voice') batch = await beginVoiceCapture(text.trim(), { token, onChange });
    else {
      const note = await createTypedSave(text.trim(), { token });
      onChange({ upsert: [note], remove: [] });
      batch = { original: note, notes: [note], token, done: Promise.resolve([note]), actionTaken: false, closed: false };
    }
    const allowed = () => {
      if (session.get() === token) return true;
      toast.dismiss(`capture-saved-${batch.original.id}`);
      return false;
    };
    const actions = {
      onEdit: async () => {
        if (!allowed()) return;
        batch.actionTaken = true; toast.dismiss(`capture-saved-${batch.original.id}`);
        const saved = await batch.done;
        if (!allowed()) return;
        if (saved.length === 1) setEditing(saved[0]);
        else if (saved.length) setEditingBatch(saved);
      },
      onUndo: async () => {
        if (!allowed() || batch.undoing) return;
        batch.undoing = true; batch.actionTaken = true;
        toast.dismiss(`capture-saved-${batch.original.id}`);
        try {
          const { removed, remaining } = await undoVoiceBatch(batch);
          onChange({ upsert: [], remove: removed });
          if (!allowed()) return;
          if (!remaining.length) toast.success(removed.length > 1 ? 'Recording undone' : 'Save undone');
          else toast.error('Could not confirm Undo for some items. You can retry safely.', { action: { label: 'Retry Undo', onClick: actions.onUndo } });
        } catch { if (allowed()) toast.error('Could not confirm Undo for this recording. Please retry.'); }
        finally { batch.undoing = false; }
      },
    };
    if (allowed()) showSavedCapture(batch, actions, method === 'voice');
    batch.done.then(() => {
      if (allowed() && !batch.actionTaken && !batch.closed) showSavedCapture(batch, actions);
    });
    pwa.onSuccessfulSave();
    return batch.original;
  };
  const update = async (id, body) => {
    const note = await api.update(id, body);
    if (['text', 'category', 'scheduled_at', 'detected_date'].some(key => Object.prototype.hasOwnProperty.call(body, key))) markNoteReviewed(note);
    setNotes(prev => prev.map(n => n.id === id ? note : n)); return note;
  };
  const remove = async id => {
    try {
      await api.remove(id); setNotes(prev => prev.filter(n => n.id !== id));
      toast('Save deleted', { action: { label: 'Undo', onClick: async () => {
        try { const note = await api.restore(id); setNotes(prev => [note, ...prev.filter(n => n.id !== id)]); }
        catch (e) { toast.error(e.message); }
      } } });
    } catch (e) { toast.error(e.message); }
  };
  const logout = async () => {
    // Guest sessions are the only way back to their data: never offer guest logout.
    if (!userRef.current || isGuestUser(userRef.current)) return;
    let remoteError = false;
    try { await disablePush(); } catch { remoteError = true; }
    try { await api.logout(); } catch { remoteError = true; }
    session.clear(); session.finishMigration(); sessionStorage.removeItem('tts_pending_draft');
    userRef.current = null; setUser(null); setNotes([]); setEditing(null); setEditingBatch(null); setError(''); setAuthOpen(false); navigate('/');
    try { await connect(); } catch { /* Guest service error is displayed inline. */ }
    if (remoteError) toast.warning('Signed out of this browser. The server could not confirm session revocation.');
  };
  return <Context.Provider value={{ user, guest, notes, loading, error, setError, online, refresh, connect, create, update, remove,
    authOpen, setAuthOpen, ensureAccess, editing, setEditing, editingBatch, setEditingBatch, logout, pwa }}>{children}</Context.Provider>;
}